import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { resolveSafeNext } from '../utils/returnTo';
import {
  Text,
  Spinner,
  Button,
  MessageBar,
  MessageBarBody,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  Person24Regular,
  LeafTwo24Regular,
  PeopleTeam24Regular,
  BoxMultiple24Regular,
  People24Regular,
  MoneyHand24Regular,
} from '@fluentui/react-icons';
import { useThemeStore } from '../store/theme.store';
import { selectRole } from '../api/auth.api';
import { useAuthStore } from '../store/auth.store';
import { getTenantConfig, type TenantConfig } from '../api/tenant.api';
import { useT } from '../i18n';

function landingFor(role: string | undefined): string {
  return role === 'INVESTOR' ? 'investor/overview' : 'dashboard';
}

const ROLE_I18N_KEYS: Record<string, string> = {
  FIELD_WORKER: 'fieldWorker',
  SUPERVISOR: 'supervisor',
  ADMIN: 'admin',
  AGRONOMIST: 'agronomist',
  SUPER_ADMIN: 'superAdmin',
  AUDITOR: 'auditor',
  WAREHOUSE_MANAGER: 'warehouseManager',
  INVESTOR: 'investor',
};

function getRoleLabel(t: ReturnType<typeof useT>, role: string): string {
  const key = ROLE_I18N_KEYS[role];
  if (key && t.auth.roles[key as keyof typeof t.auth.roles]) {
    return t.auth.roles[key as keyof typeof t.auth.roles];
  }
  return role;
}

const ROLE_ICON_MAP: Record<string, typeof Person24Regular> = {
  'ADMIN': Person24Regular,
  'AGRONOMIST': LeafTwo24Regular,
  'SUPERVISOR': PeopleTeam24Regular,
  'WAREHOUSE_MANAGER': BoxMultiple24Regular,
  'FIELD_WORKER': People24Regular,
  'SUPER_ADMIN': Person24Regular,
  'AUDITOR': Person24Regular,
  'HR': People24Regular,
  'GUARD': Person24Regular,
  'INVESTOR': MoneyHand24Regular,
};

const useStyles = makeStyles({
  root: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    padding: '40px',
    width: '420px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    boxShadow: tokens.shadow16,
    alignItems: 'center',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
  },
  logo: {
    width: '56px',
    height: '56px',
    borderRadius: '4px',
    objectFit: 'cover',
  },
  title: { fontSize: '18px', fontWeight: 600 },
  subtitle: {
    color: tokens.colorNeutralForeground3,
    fontSize: '13px',
    textAlign: 'center',
  },
  roleGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    width: '100%',
  },
  roleCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    backgroundColor: tokens.colorNeutralBackground1,
    transition: 'border-color 0.15s, background 0.15s',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
      border: `1px solid ${tokens.colorBrandStroke1}`,
    },
  },
  roleCardSelected: {
    border: `1px solid ${tokens.colorBrandBackground}`,
    backgroundColor: tokens.colorBrandBackground2,
  },
  roleIcon: {
    color: tokens.colorBrandForeground1,
    flexShrink: 0,
  },
  roleText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
  },
  roleLabel: {
    fontSize: '13px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  roleDesc: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
  },
  error: { color: tokens.colorStatusDangerForeground1, fontSize: '13px', textAlign: 'center' },
});

export function RoleSelectionPage() {
  const s = useStyles();
  const t = useT();
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const safeNext = resolveSafeNext(searchParams.get('next'), tenantId);
  const { user, setSession, logout } = useAuthStore();
  const autoRan = useRef(false);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tenantConfig, setTenantConfig] = useState<TenantConfig | null>(null);

  const roles: string[] = user?.roles ?? [];
  const { setMode } = useThemeStore();

  useEffect(() => {
    if (!tenantId) return;
    getTenantConfig(tenantId)
      .then(setTenantConfig)
      .catch(() => { /* silently fail */ });
  }, [tenantId]);

  async function handleSelectRole(role: string) {
    setSelectedRole(role);
    setLoading(true);
    setError(null);
    try {
      const response = await selectRole(role);
      setSession(response.token, response.user);
      const prefs = response.user?.preferences;
      if (prefs?.theme) setMode(prefs.theme);
      navigate(safeNext ?? `/${tenantId}/${landingFor(response.user?.activeRole ?? role)}`, { replace: true });
    } catch (e: any) {
      // Fuera del horario permitido de la empresa: el servidor dice el rango, no un error genérico.
      const data = e?.response?.data;
      setError(data?.error === 'OUTSIDE_ACCESS_HOURS' && typeof data.message === 'string'
        ? data.message
        : t.auth.selectRoleError);
    } finally {
      setLoading(false);
    }
  }

  // Rol ya activo → entrar directo; un solo rol → seleccionarlo solo.
  useEffect(() => {
    if (autoRan.current || !user) return;
    if (user.activeRole) {
      autoRan.current = true;
      navigate(safeNext ?? `/${tenantId}/${landingFor(user.activeRole)}`, { replace: true });
    } else if (roles.length === 1) {
      autoRan.current = true;
      void handleSelectRole(roles[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tenantId]);

  function handleSignOut() {
    logout();
    navigate(`/${tenantId ?? 'demo'}/login`, { replace: true });
  }

  const noRoles = roles.length === 0 && !user?.activeRole;

  return (
    <div className={s.root}>
      <div className={s.card}>
        <div className={s.header}>
          {tenantConfig?.logoUrl ? (
            <img
              src={tenantConfig.logoUrl}
              alt={tenantConfig.displayName}
              className={s.logo}
            />
          ) : (
            <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <circle cx="28" cy="28" r="28" fill="#16a34a" />
              <line x1="28" y1="42" x2="28" y2="24" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
              <path d="M28 30 Q18 22 20 14 Q30 16 28 30Z" fill="white" opacity="0.95" />
              <path d="M28 26 Q38 20 36 12 Q26 14 28 26Z" fill="white" opacity="0.75" />
              <circle cx="24" cy="43" r="1.5" fill="white" opacity="0.6" />
              <circle cx="28" cy="45" r="1.5" fill="white" opacity="0.6" />
              <circle cx="32" cy="43" r="1.5" fill="white" opacity="0.6" />
            </svg>
          )}
          <Text size={600} weight="semibold" className={s.title}>
            {tenantConfig?.displayName || t.auth.roleSelectionTitle}
          </Text>
        </div>

        <Text className={s.subtitle}>
          {noRoles ? t.auth.noRoleAssigned : t.auth.roleSelectionSubtitle}
        </Text>

        {noRoles && (
          <Button appearance="primary" onClick={handleSignOut}>
            {t.auth.noRoleSignOut}
          </Button>
        )}

        {error && (
          <MessageBar intent="error">
            <MessageBarBody>
              <Text>{error}</Text>
            </MessageBarBody>
          </MessageBar>
        )}

        {loading ? (
          <Spinner size="medium" />
        ) : (
          <div className={s.roleGrid}>
            {roles.map((role) => {
              const Icon = ROLE_ICON_MAP[role] ?? Person24Regular;
              return (
                <div
                  key={role}
                  className={`${s.roleCard} ${selectedRole === role ? s.roleCardSelected : ''}`}
                  onClick={() => handleSelectRole(role)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === 'Enter' && handleSelectRole(role)}
                >
                  <Icon className={s.roleIcon} />
                  <div className={s.roleText}>
                    <span className={s.roleLabel}>{getRoleLabel(t, role)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
